import React, { useState } from 'react';

interface CreateTableFormProps {
  tableName: string;
  columnCount: number;
  setTableName: (name: string) => void;
  setColumnCount: (count: number) => void;
  handleCreateTable: () => void;
  handleNext: () => void;
  hasTables: boolean;
}

const TABLE_NAME_REGEX = /^[a-zA-Z_]*$/;

const CreateTableForm: React.FC<CreateTableFormProps> = ({
  tableName,
  columnCount,
  setTableName,
  setColumnCount,
  handleCreateTable,
  handleNext,
  hasTables,
}) => {
  const [hasError, setHasError] = useState(false);
  const [columnInput, setColumnInput] = useState(String(columnCount));

  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (TABLE_NAME_REGEX.test(value)) {
      setTableName(value);
      setHasError(false);
    }
  };

  const handleColumnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setColumnInput(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 1) setColumnCount(parsed);
  };

  const handleColumnBlur = () => {
    const parsed = parseInt(columnInput, 10);
    if (isNaN(parsed) || parsed < 1) {
      setColumnInput('1');
      setColumnCount(1);
    } else {
      setColumnInput(String(parsed));
    }
  };

  const handleCreateClick = () => {
    if (!tableName || tableName.trim() === '') {
      setHasError(true);
    } else {
      setHasError(false);
      handleCreateTable();
    }
  };

  const btnBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 14px',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: 'Consolas, monospace',
    borderRadius: '5px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  return (
    <div>
      <div className="form-group">
        <label htmlFor="tableName"><strong>Nombre de la tabla</strong></label>
        <input
          type="text"
          className={`form-control ${hasError ? 'is-invalid' : ''}`}
          id="tableName"
          placeholder="Ingresa el nombre de la tabla"
          value={tableName}
          onChange={handleTableNameChange}
        />
        {hasError && (
          <div className="invalid-feedback">Favor de poner un nombre a la tabla.</div>
        )}
        <small className="text-muted">Solo letras y guión bajo. Ej: <code>mi_tabla</code></small>
      </div>

      <div className="form-group mt-3">
        <label htmlFor="columnCount"><strong>Número de columnas</strong></label>
        <input
          type="text"
          inputMode="numeric"
          className="form-control"
          id="columnCount"
          value={columnInput}
          onFocus={(e) => e.target.select()}
          onChange={handleColumnChange}
          onBlur={handleColumnBlur}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button
          type="button"
          style={{ ...btnBase, background: '#1a7fd4', color: '#fff' }}
          onClick={handleCreateClick}
          onMouseEnter={e => (e.currentTarget.style.background = '#1569b5')}
          onMouseLeave={e => (e.currentTarget.style.background = '#1a7fd4')}
        >
          + Crear Tabla
        </button>

        <button
          type="button"
          style={{
            ...btnBase,
            background: 'transparent',
            color: hasTables ? '#555' : '#bbb',
            border: '1px solid',
            borderColor: hasTables ? '#ccc' : '#e0e0e0',
            cursor: hasTables ? 'pointer' : 'not-allowed',
          }}
          onClick={handleNext}
          disabled={!hasTables}
          title={!hasTables ? 'Primero debes crear al menos una tabla' : ''}
          onMouseEnter={e => { if (hasTables) e.currentTarget.style.background = '#f0f0f0'; }}
          onMouseLeave={e => { if (hasTables) e.currentTarget.style.background = 'transparent'; }}
        >
          Generar Proyecto
        </button>
      </div>
    </div>
  );
};

export default CreateTableForm;